import {
  PaymentAPI,
  PaymentProvider,
  PaymentState,
  SetupRecurringPaymentMethodAndPollOptions,
} from '../../typings/Payment'
import { getGoogleSearchParam } from '../../lib/get-google-search-param'
import setupRecurringPaymentMethod from './setup-recurring-payment-method'
import pollPayment from './poll-payment'

const setupRecurringPaymentMethodAndPoll = async (
  paymentProvider: PaymentProvider,
  paymentAPI: PaymentAPI,
  { orderId, options, lastPaymentAttempt }: SetupRecurringPaymentMethodAndPollOptions,
) => {
  const { payment, selectedPaymentMethod, ibanElement, verifyWith, user } = options
  const { error, data } = await setupRecurringPaymentMethod(paymentAPI, {
    paymentId: payment.id,
  })

  const paymentSetupIntent = data?.paymentSetupIntent

  if (!paymentSetupIntent?.clientSecret) {
    throw new Error('No client secret was returned for setup recurring payment method api')
  }

  if (error) {
    return { wasSuccessful: false, error }
  }

  const { stripe } = paymentProvider

  if (!stripe) {
    throw new Error('Stripe provider was not found')
  }

  // Sepa + iDeal flow
  if (verifyWith) {
    const userFullName = `${user.firstName} ${user.lastName}`

    const returnUrl = new URL(
      `${
        window.location.href
      }&paymentInProgress=true&isIdealSetupFlow=true&lastPaymentAttempt=${lastPaymentAttempt.getTime()}`,
    )

    const trackingParam = getGoogleSearchParam()

    if (trackingParam) {
      const [trackingName, trackingValue] = trackingParam.split('=')
      returnUrl.searchParams.append(trackingName, trackingValue)
    }

    const { error: idealSetupError } = await stripe.confirmIdealSetup(paymentSetupIntent.clientSecret, {
      payment_method: {
        ideal: { bank: undefined },
        billing_details: {
          name: userFullName,
          email: user.email,
        },
      },
      return_url: returnUrl.toString(),
    })

    if (idealSetupError) {
      return { wasSuccessful: false, error: idealSetupError.message }
    }
  }

  // Direct Sepa flow
  if (selectedPaymentMethod.bankAccountHolder && ibanElement) {
    const { error: sepaDirectSetupError } = await stripe.confirmSepaDebitSetup(paymentSetupIntent.clientSecret, {
      payment_method: {
        sepa_debit: ibanElement,
        billing_details: {
          name: selectedPaymentMethod.bankAccountHolder,
          email: user.email,
        },
      },
    })

    if (sepaDirectSetupError) {
      return { wasSuccessful: false, error: sepaDirectSetupError.message }
    }
  }

  // the payment state can either transition to
  // - `ON_HOLD` if a mandate was created initially or to
  // - `IN_PROGRESS` if the verified mandate is charge immediately
  return pollPayment(paymentAPI, {
    orderId,
    lastPaymentAttempt,
    additionalExpectedPaymentStates: [PaymentState.PAYMENT_STATE_ON_HOLD, PaymentState.PAYMENT_STATE_IN_PROGRESS],
  })
}

export default setupRecurringPaymentMethodAndPoll
